import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { Logo } from '../assets/Logo';
import { PrimaryButton } from './PrimaryButton';
import { ProfileMenu } from './ProfileMenu';
import { SubscriptionBanner, clearSubscriptionStatusCache } from './SubscriptionBanner';
import { useWhatsApp } from '../whatsapp/WhatsAppContext';
import { useInbox } from '../whatsapp/InboxProvider';
import { ComingSoonDialog } from './whatsapp/ComingSoonDialog';

interface NavbarProps {
  showLogout?: boolean;
}

export const Navbar = ({ showLogout = false }: NavbarProps) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { enabled: whatsappEnabled, comingSoon } = useWhatsApp();
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const { counters } = useInbox();
  const unanswered = counters?.unanswered ?? 0;

  const handleNewPatient = () => {
    setMenuOpen(false);
    navigate('/patient');
  };

  const handleImport = () => {
    setMenuOpen(false);
    navigate('/import-patients');
  };

  const handleLogoClick = () => {
    navigate('/home');
  };

  const handleInbox = () => {
    setMenuOpen(false);
    navigate('/whatsapp');
  };

  const handleComingSoon = () => {
    setMenuOpen(false);
    setComingSoonOpen(true);
  };

  const handleSettings = () => {
    setMenuOpen(false);
    navigate('/settings');
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      clearSubscriptionStatusCache();
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <>
    <header className="relative flex w-full items-center justify-between">
      {/* Logo */}
      <div className="flex items-center gap-2 cursor-pointer" onClick={handleLogoClick}>
        <Logo className="h-10 w-auto" />
      </div>

      {/* Desktop nav */}
      <div className="hidden md:flex items-center gap-4">
        {whatsappEnabled && (
          <button
            onClick={handleInbox}
            className="flex h-12 items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-slate-700 shadow-[0_4px_12px_0_rgba(0,0,0,0.05)] transition-all hover:bg-slate-50"
          >
            <span className="font-bold">Bandeja</span>
            {unanswered > 0 && (
              <span className="rounded-full bg-primary px-[7px] py-px font-geist-mono text-xs text-white">{unanswered}</span>
            )}
          </button>
        )}
        {comingSoon && (
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={handleComingSoon}
            className="flex h-12 items-center gap-2.5 rounded-xl border border-slate-200 bg-white py-0 pl-5 pr-3 text-slate-700 shadow-[0_4px_12px_0_rgba(0,0,0,0.05)] transition-all hover:bg-slate-50"
          >
            <span className="font-bold">Bandeja</span>
            <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">Próximamente</span>
          </button>
        )}
        {showLogout && (
          <button
            onClick={handleImport}
            className="flex h-12 items-center gap-2 rounded-xl bg-slate-200 px-5 text-slate-700 shadow-[0_4px_12px_0_rgba(0,0,0,0.05)] transition-all hover:bg-slate-300"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
            <span className="font-bold">Importar</span>
          </button>
        )}
        <PrimaryButton
          onClick={handleNewPatient}
          className="h-12 px-5"
          icon={
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
            </svg>
          }
        >
          Nuevo Paciente
        </PrimaryButton>
        {showLogout && <ProfileMenu onLogout={handleLogout} />}
      </div>

      {/* Mobile hamburger button */}
      <button
        className="md:hidden flex items-center justify-center w-11 h-11 rounded-xl bg-white border border-slate-200 shadow-sm hover:bg-slate-50 transition-colors relative overflow-hidden"
        style={{ minWidth: 44, minHeight: 44 }}
        onClick={() => setMenuOpen((prev) => !prev)}
        aria-label="Abrir menú"
        aria-expanded={menuOpen}
      >
        {/* menu icon */}
        <span
          className="material-symbols-outlined select-none absolute transition-all duration-200 ease-out"
          style={{
            fontSize: 24,
            color: '#334155',
            fontVariationSettings: '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24',
            opacity: menuOpen ? 0 : 1,
            transform: menuOpen ? 'rotate(90deg) scale(0.7)' : 'rotate(0deg) scale(1)',
          }}
        >
          menu
        </span>
        {/* close icon */}
        <span
          className="material-symbols-outlined select-none absolute transition-all duration-200 ease-out"
          style={{
            fontSize: 24,
            color: '#334155',
            fontVariationSettings: '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24',
            opacity: menuOpen ? 1 : 0,
            transform: menuOpen ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.7)',
          }}
        >
          close
        </span>
      </button>

      {/* Mobile dropdown menu — always rendered, animated via opacity + scale */}
      <div
        className="md:hidden absolute top-full left-0 right-0 mt-2 z-50 flex flex-col gap-3 rounded-xl bg-white p-4 shadow-lg border border-slate-100 origin-top transition-all duration-200 ease-out"
        style={{
          opacity: menuOpen ? 1 : 0,
          transform: menuOpen ? 'scaleY(1) translateY(0)' : 'scaleY(0.92) translateY(-8px)',
          pointerEvents: menuOpen ? 'auto' : 'none',
        }}
      >
        {whatsappEnabled && (
          <button
            onClick={handleInbox}
            className="flex w-full h-12 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-slate-700 transition-all hover:bg-slate-50"
          >
            <span className="font-bold">Bandeja</span>
            {unanswered > 0 && (
              <span className="rounded-full bg-primary px-[7px] py-px font-geist-mono text-xs text-white">{unanswered}</span>
            )}
          </button>
        )}
        {comingSoon && (
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={handleComingSoon}
            className="flex w-full h-12 items-center justify-center gap-2.5 rounded-xl border border-slate-200 bg-white px-5 text-slate-700 transition-all hover:bg-slate-50"
          >
            <span className="font-bold">Bandeja</span>
            <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">Próximamente</span>
          </button>
        )}
        <PrimaryButton
          onClick={handleNewPatient}
          className="w-full justify-center h-12"
          icon={
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
            </svg>
          }
        >
          Nuevo Paciente
        </PrimaryButton>
        <button
          onClick={handleImport}
          className="flex w-full h-12 items-center justify-center gap-2 rounded-xl bg-slate-100 px-5 text-slate-700 transition-all hover:bg-slate-200"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
          </svg>
          <span className="font-bold">Importar Pacientes</span>
        </button>
        {showLogout && (
          <>
            <button
              onClick={handleSettings}
              className="flex w-full h-12 items-center justify-center gap-2 rounded-xl bg-slate-100 px-5 text-slate-700 transition-all hover:bg-slate-200"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="font-bold">Configuración</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex w-full h-12 items-center justify-center gap-2 rounded-xl bg-slate-100 px-5 text-slate-700 transition-all hover:bg-slate-200"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="font-bold">Cerrar Sesión</span>
            </button>
          </>
        )}
      </div>
    </header>
    <SubscriptionBanner />
    {comingSoonOpen && <ComingSoonDialog onClose={() => setComingSoonOpen(false)} />}
    </>
  );
};
